import {createAsyncThunk, createEntityAdapter, createSlice} from "@reduxjs/toolkit";
import {apiRequest} from "../api/apiRequest";
import type {RootState} from "./store";
import type {TenantSummaryResponse} from "../types/tenant";
import type {TenantCreateRequest} from "../types/tenant-create";

type AsyncStatus = "idle" | "loading" | "succeeded" | "failed";

const tenantsAdapter = createEntityAdapter<TenantSummaryResponse>({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

export type TenantsState = ReturnType<typeof tenantsAdapter.getInitialState> & {
  status: AsyncStatus;
  error: string | null;
  createStatus: AsyncStatus;
  updateStatus: AsyncStatus;
  deleteStatus: AsyncStatus;
};

const initialState: TenantsState = tenantsAdapter.getInitialState({
  status: "idle",
  error: null,
  createStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
});

export const fetchTenants = createAsyncThunk<TenantSummaryResponse[], void>("tenants/fetchTenants", async () => {
  return apiRequest<TenantSummaryResponse[]>({path: "admin/tenants"});
});

export const createTenant = createAsyncThunk<TenantSummaryResponse, TenantCreateRequest>(
  "tenants/createTenant",
  async (body) => {
    return apiRequest<TenantSummaryResponse>({
      path: "admin/tenants",
      method: "POST",
      body,
    });
  },
);

export const updateTenant = createAsyncThunk<TenantSummaryResponse, { id: string; name: string; promoCode?: string }>(
  "tenants/updateTenant",
  async ({id, ...tenantData}) => {
    return apiRequest<TenantSummaryResponse>({
      path: `admin/tenants/${id}`,
      method: "PUT",
      body: tenantData,
    });
  },
);

export const deleteTenant = createAsyncThunk<void, string>("tenants/deleteTenant", async (tenantId) => {
  return apiRequest<void>({
    path: `admin/tenants/${tenantId}`,
    method: "DELETE",
  });
});

export const tenantsSlice = createSlice({
  name: "tenants",
  initialState,
  reducers: {
    reset(state) {
      tenantsAdapter.removeAll(state);
      state.status = "idle";
      state.error = null;
      state.createStatus = "idle";
      state.updateStatus = "idle";
      state.deleteStatus = "idle";
    },
    updateTenantInCache(
      state,
      action: { payload: { id: string; changes: Partial<TenantSummaryResponse> } },
    ) {
      tenantsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: action.payload.changes,
      });
    },
    removeTenantFromCache(state, action: { payload: string }) {
      tenantsAdapter.removeOne(state, action.payload);
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchTenants.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.status = "succeeded";
        tenantsAdapter.setAll(state, action.payload);
      })
      .addCase(fetchTenants.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load tenants";
      })
      .addCase(createTenant.pending, (state) => {
        state.createStatus = "loading";
        state.error = null;
      })
      .addCase(createTenant.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        tenantsAdapter.addOne(state, action.payload);
      })
      .addCase(createTenant.rejected, (state, action) => {
        state.createStatus = "failed";
        state.error = action.error.message ?? "Failed to create tenant";
      })
      .addCase(updateTenant.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(updateTenant.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        tenantsAdapter.updateOne(state, {id: action.payload.id, changes: action.payload});
      })
      .addCase(updateTenant.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error = action.error.message ?? "Failed to update tenant";
      })
      .addCase(deleteTenant.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteTenant.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        // Remove the tenant from the state
        tenantsAdapter.removeOne(state, action.meta.arg);
      })
      .addCase(deleteTenant.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.error.message ?? "Failed to delete tenant";
      });
  },
});

export const {reset, updateTenantInCache, removeTenantFromCache} = tenantsSlice.actions;

export const tenantsSelectors = tenantsAdapter.getSelectors<RootState>((s) => s.tenants);
